import { Suspense, lazy } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { LoginRequired, NoCatalogRedirectToHome } from './middlewares';
import * as path from './paths';

// LAYOUTS
const MainLayout = lazy(() => import('../views/layouts/mainLayout/MainLayout'));
const AuthenticationLayout = lazy(() => import('../views/layouts/authLayout/AuthenticationLayout'));
const AdminLayout = lazy(() => import('views/layouts/adminLayout/AdminLayout'));
const AccountLayout = lazy(() => import('../views/layouts/accountLayout/AccountLayout'));
//// PAGES
// Main Pages
const HomePage = lazy(() => import('views/pages/main/homePage/HomePage'));
const BrandPage = lazy(() => import('../views/pages/main/tenantFeedPage/TenantFeedPage'));
const DashboardSalePage = lazy(() => import('views/pages/main/dashboards/dashboardSalePage/DashboardSalePage'));
const DashboardPredictionPage = lazy(() => import('views/pages/main/dashboards/dashboardPredictionPage/DashboardPredictionPage'));
// Catalog
const CatalogPage = lazy(() => import('views/pages/main/catalog/catalogPage/CatalogPage'));
const AddProductsPage = lazy(() => import('views/pages/main/catalog/addProductsPage/AddProductsPage'));
const CatalogDownloadPage = lazy(() => import('views/pages/main/catalog/catalogDownloadPage/CatalogDownloadPage'));
const ProductPage = lazy(() => import('views/pages/main/catalog/productPage/ProductPage'));
const MultimediaPage = lazy(() => import('views/pages/main/multimediaPage/MultimediaPage'));
const MultimediaVideosPage = lazy(() => import('views/pages/main/multimediaPage/multimediaVideos/MultimediaVideos'));
const MultimediaImagesPage = lazy(() => import('views/pages/main/multimediaPage/multimediaImages/MultimediaImages'));
const MultimediaBannersPage = lazy(() => import('views/pages/main/multimediaPage/multimediaBanners/MultimediaBanners'));
const RegisterBrand = lazy(() => import('views/pages/main/registerBrandPage/RegisterBrandPage'));
const ThanksForRegister = lazy(() => import('views/pages/main/thanksForRegister/ThanksForRegister'));
const NotificationsPage = lazy(() => import('views/pages/main/notificationsPage/NotificationsPage'));
const SalesPage = lazy(() => import('views/pages/main/salesPage/SalesPage'));
const SalesMatching = lazy(() => import('views/pages/main/salesPage/salesMatching/SalesMatching'));
// Import file
const ImportFiles = lazy(() => import('views/pages/main/importPage/importFiles/ImportFiles'));
const ImportFilesHistory = lazy(() => import('views/pages/main/importPage/importFilesHistory/ImportFilesHistory'));
// Operations
const DeliveryNotes = lazy(() => import('views/pages/main/transactions/deliveryNotes/DeliveryNotes'));
const Bills = lazy(() => import('views/pages/main/transactions/bills/Bills'));
const Orders = lazy(() => import('views/pages/main/transactions/orders/Orders'));
// Auth Pages
const SignupPage = lazy(() => import('views/pages/authentication/signupPage/SignupPage'));
const LoginRedirect = lazy(() => import('views/pages/authentication/LoginRedirect'));
// Profile pages
const ProfilePage = lazy(() => import('views/pages/main/accountPage/profilePage/ProfilePage'));
const CompanyDataPage = lazy(() => import('views/pages/main/accountPage/companyDataPage/CompanyDataPage'));
const MainContactsPage = lazy(() => import('views/pages/main/accountPage/mainContactsPage/MainContactsPage'));
const MyBrandsPage = lazy(() => import('views/pages/main/accountPage/myBrandsPage/MyBrandsPage'));
const PointsOfSalePage = lazy(() => import('views/pages/main/accountPage/pointsOfSalePage/PointsOfSalePage'));
const SelectedBrandPage = lazy(() => import('views/pages/main/accountPage/brandpage/BrandPage'));
//Config
const SettingsPage = lazy(() => import('views/pages/main/settingsPage/SettingsPage'));
const UserConfig = lazy(() => import('views/pages/main/settingsPage/usersPage/UsersPage'));
const DataIntegrationPage = lazy(() => import('views/pages/main/settingsPage/dataIntegrationPage/DataIntegrationPage'));
const CreateUpdateMapping = lazy(() => import('views/pages/main/settingsPage/dataIntegrationPage/components/createUpdateMapping/CreateUpdateMapping'));
const EditApiConnection = lazy(() => import('views/pages/main/settingsPage/dataIntegrationPage/components/connections/components/editApiConnection/EditApiConnection'));
const ApiConfiguration = lazy(() => import('views/pages/main/settingsPage/dataIntegrationPage/components/connections/components/apiConfiguration/ApiConfiguration'));
const MapFieldsConfiguration = lazy(() => import('views/pages/main/settingsPage/dataIntegrationPage/components/connections/components/mapFieldsConfiguration/MapFieldsConfiguration'));
const MapConfiguration = lazy(() => import('views/pages/main/settingsPage/dataIntegrationPage/components/connections/components/mapConfiguration/MapConfiguration'));
const EditMap = lazy(() => import('views/pages/main/settingsPage/dataIntegrationPage/components/connections/components/editMap/EditMap'));
// Admin
const AdminHome = lazy(() => import('views/pages/admin/adminHome/AdminHome'));
const AdminAPIConfiguration = lazy(() => import('views/pages/admin/adminAPIConfiguration/AdminAPIConfiguration'));
const AdminGeneralConfiguration = lazy(() => import('views/pages/admin/adminGeneralConfiguration/AdminGeneralConfiguration'));
const AdminDataIntegration = lazy(() => import('views/pages/admin/adminDataIntegration/AdminDataIntegration'));
// Error
const ErrorXXX = lazy(() => import('views/pages/messages/errorXXX/ErrorXXX'));
const ComingSoon = lazy(() => import('views/pages/messages/comingSoon/ComingSoon'));

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <LoginRequired>
        <NoCatalogRedirectToHome>
          <Lazy><MainLayout /></Lazy>
        </NoCatalogRedirectToHome>
      </LoginRequired>
    ),
    children: [
      {
        path: '/',
        element: <Lazy><HomePage /></Lazy>,
      },
      {
        path: '/brand-feed/:tenantId/:brandId',
        element: <Lazy><BrandPage /></Lazy>,
      },
      {
        path: path.dashboardSale,
        element: <Lazy><DashboardSalePage /></Lazy>,
      },
      {
        path: path.dashboardPrediction,
        element: <Lazy><DashboardPredictionPage /></Lazy>,
      },
      // CATALOG
      {
        path: `${path.catalog}`,
        element: <Lazy><CatalogPage /></Lazy>,
      },
      {
        path: `${path.catalogProduct}/:productId/:brandId`,
        element: <Lazy><ProductPage /></Lazy>,
      },
      {
        path: `${path.catalogAddProduct}`,
        element: <Lazy><AddProductsPage /></Lazy>,
      },
      {
        path: path.catalogDownload,
        element: <Lazy><CatalogDownloadPage /></Lazy>,
      },
      // MULTIMEDIA
      {
        path: 'multimedia',
        element: <Lazy><MultimediaPage /></Lazy>,
        children: [
          {
            path: 'videos',
            element: <Lazy><MultimediaVideosPage /></Lazy>,
          },
          {
            path: 'images',
            element: <Lazy><MultimediaImagesPage /></Lazy>,
          },
          {
            path: 'banners',
            element: <Lazy><MultimediaBannersPage /></Lazy>,
          },
        ],
      },
      // SALES
      {
        path: path.sales,
        element: <Lazy><SalesPage /></Lazy>,
        children: [
          {
            path: path.salesMatching,
            element: <Lazy><SalesMatching /></Lazy>,
          }
        ],
      },
      // ACCOUNT
      {
        path: 'account',
        element: <Lazy><AccountLayout /></Lazy>,
        children: [
          {
            path: 'profile',
            element: <Lazy><ProfilePage /></Lazy>,
          },
          {
            path: 'company',
            element: <Lazy><CompanyDataPage /></Lazy>,
          },
          {
            path: 'contacts',
            element: <Lazy><MainContactsPage /></Lazy>,
          },
          {
            path: 'my-brands',
            element: <Lazy><MyBrandsPage /></Lazy>,
          },
          {
            path: 'brand/:brandId/:tenantId',
            element: <Lazy><SelectedBrandPage /></Lazy>,
          },
          {
            path: 'points-of-sale',
            element: <Lazy><PointsOfSalePage /></Lazy>,
          },
        ],
      },
      // IMPORT FILES
      {
        path: path.importFile,
        element: <Lazy><ImportFiles /></Lazy>,
      },
      {
        path: `${path.importFile}/:audienceId`,
        element: <Lazy><ImportFiles /></Lazy>,
      },
      {
        path: `${path.importHistory}`,
        element: <Lazy><ImportFilesHistory /></Lazy>,
      },
      // CONFIG
      {
        path: path.settings,
        element: <Lazy><SettingsPage /></Lazy>,
      },
      {
        path: path.settingsUsers,
        element: <Lazy><UserConfig /></Lazy>,
      },
      {
        path: path.settingsChronos,
        element: <Lazy><ComingSoon /></Lazy>,
      },
      {
        path: path.settingsDevices,
        element: <Lazy><ComingSoon /></Lazy>,
      },
      {
        path: path.settingsTouchScreen,
        element: <Lazy><ComingSoon /></Lazy>,
      },
      {
        path: path.settingsSystemSettings,
        element: <Lazy><ComingSoon /></Lazy>,
      },
      // {
      //   path: path.settingsMeta,
      //   element: <Lazy><MetaPage /></Lazy>,
      // },

      {
        path: `${path.settingsMetaEditAPI}/:connectorType/:connectorId`,
        element: <Lazy><EditApiConnection /></Lazy>,
      },
      {
        path: `${path.settingsMetaGeneralConfigurationAPI}/:connectorType`,
        element: <Lazy><ApiConfiguration /></Lazy>,
      },
      {
        path: path.settingsMetaMapFieldsConfigurationAPI,
        element: <Lazy><MapFieldsConfiguration /></Lazy>,
      },
      {
        path: path.settingsMetaMaps,
        element: <Lazy><MapConfiguration /></Lazy>,
      },
      {
        path: `${path.settingMetaEditMap}/:mapId`,
        element: <Lazy><EditMap /></Lazy>,
      },
      {
        path: `${path.settingMetaEditMap}/:mapId/:mapFieldId`,
        element: <Lazy><EditMap /></Lazy>,
      },
      {
        path: path.settingsBilling,
        element: <Lazy><ComingSoon /></Lazy>,
      },
      {
        path: path.settingsSupport,
        element: <Lazy><ComingSoon /></Lazy>,
      },
      {
        path: path.settingsDataIntegration,
        element: <Lazy><DataIntegrationPage /></Lazy>,
      },
      {
        path: path.settingsCreateUpdateMapping, // ?mapId=123&importToReprocessId=456
        element: <Lazy><CreateUpdateMapping /></Lazy>,
      },
      // OPERATIONS
      {
        path: path.deliveryNotes,
        element: <Lazy><DeliveryNotes /></Lazy>,
      },
       {
        path: path.bills,
        element: <Lazy><Bills /></Lazy>,
      },
       {
        path: path.orders,
        element: <Lazy><Orders /></Lazy>,
      },
      {
        path: path.inventory,
        element: <Lazy><ComingSoon /></Lazy>,
      },
      {
        path: path.budgets,
        element: <Lazy><ComingSoon /></Lazy>,
      },
      // NOTIFICATIONS
      {
        path: 'notifications',
        element: <Lazy><NotificationsPage /></Lazy>,
      },
      // ERROR
      {
        path: '*',
        element: <Lazy><ErrorXXX code="404" /></Lazy>,
      },
    ],
  },
  {
    path: 'authentication',
    element: <Lazy><AuthenticationLayout /></Lazy>,
    children: [
      {
        path: 'signup',
        element: <Lazy><SignupPage /></Lazy>,
      },
      {
        path: 'signup-brand',
        element: <Lazy><RegisterBrand /></Lazy>,
      },
      {
        path: 'thanks',
        element: <Lazy><ThanksForRegister /></Lazy>,
      },
      {
        path: 'login',
        element: <Lazy><LoginRedirect /></Lazy>,
      },
    ],
  },
  {
    path: 'admin',
    element: <Lazy><AdminLayout /></Lazy>,
    children: [
      {
        path: 'home',
        element: <Lazy><AdminHome /></Lazy>,
      },
      {
        path: 'api-configuration',
        element: <Lazy><AdminAPIConfiguration /></Lazy>,
      },
      {
        path: `api-configuration/:mapFieldId`,
        element: <Lazy><AdminAPIConfiguration /></Lazy>,
      },
      {
        path: 'general-configuration',
        element: <Lazy><AdminGeneralConfiguration /></Lazy>,
      },
      {
        path: 'data-integration',
        element: <Lazy><AdminDataIntegration /></Lazy>,
      },
    ],
  },
]);

function Lazy({ children }: any) {
  return <Suspense fallback={<></>}>{children}</Suspense>;
}

export default router;
